/** HR Dashboard — overview KPIs + trends, estimate-vs-actual, AI weekly digest. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Gauge, Timer, Sparkles, RefreshCw, MessageCircleQuestion, Send, UserSearch } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { PersonChip } from "@/components/PersonChip";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };

export default function HrDashboard() {
  const { activeWorkspaceId: wsId } = useAuth();
  return (
    <>
      <PageHeader eyebrow="HRMS" title="HR Dashboard" description="Utilization, compliance, estimate accuracy and AI-powered weekly digests." testid="hr-dashboard-header" />
      <div className="px-4 sm:px-6 md:px-10 pb-10">
        <Tabs defaultValue="overview">
          <TabsList className="rounded-none flex-wrap h-auto">
            <TabsTrigger value="overview" className="rounded-none" data-testid="hrd-tab-overview"><Gauge className="w-4 h-4 mr-2" />Overview</TabsTrigger>
            <TabsTrigger value="estimates" className="rounded-none" data-testid="hrd-tab-estimates"><Timer className="w-4 h-4 mr-2" />Estimate vs actual</TabsTrigger>
            <TabsTrigger value="digest" className="rounded-none" data-testid="hrd-tab-digest"><Sparkles className="w-4 h-4 mr-2" />AI digest</TabsTrigger>
            <TabsTrigger value="ask" className="rounded-none" data-testid="hrd-tab-ask"><MessageCircleQuestion className="w-4 h-4 mr-2" />Ask AI</TabsTrigger>
            <TabsTrigger value="empai" className="rounded-none" data-testid="hrd-tab-empai"><UserSearch className="w-4 h-4 mr-2" />Employee AI</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-5"><OverviewTab wsId={wsId} /></TabsContent>
          <TabsContent value="estimates" className="mt-5"><EstimatesTab wsId={wsId} /></TabsContent>
          <TabsContent value="digest" className="mt-5"><DigestTab wsId={wsId} /></TabsContent>
          <TabsContent value="ask" className="mt-5"><AskTab wsId={wsId} /></TabsContent>
          <TabsContent value="empai" className="mt-5"><EmployeeAITab wsId={wsId} /></TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function RangeBar({ from, setFrom, to, setTo, onApply, testid }) {
  return (
    <div className="flex items-center gap-2 mb-5">
      <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none w-40" data-testid={`${testid}-from`} />
      <span className="text-sm text-muted-foreground">→</span>
      <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none w-40" data-testid={`${testid}-to`} />
      <Button size="sm" className="rounded-none" onClick={onApply} data-testid={`${testid}-apply`}>Apply</Button>
    </div>
  );
}

function Stat({ label, value, sub, testid, tone }) {
  return (
    <div className="border p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${tone || ""}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function OverviewTab({ wsId }) {
  const [from, setFrom] = useState(daysAgo(14));
  const [to, setTo] = useState(daysAgo(0));
  const [ov, setOv] = useState(null);
  const [trends, setTrends] = useState([]);
  const load = () => {
    api.get(`/workspaces/${wsId}/hr-dashboard/overview?from=${from}&to=${to}`).then((r) => setOv(r.data)).catch((e) => toast.error(apiErrorMessage(e)));
    api.get(`/workspaces/${wsId}/hr-dashboard/trends?from=${from}&to=${to}`).then((r) => setTrends(r.data.days)).catch(() => {});
  };
  useEffect(() => { load(); }, [wsId]); // eslint-disable-line
  if (!ov) return <RangeBar from={from} setFrom={setFrom} to={to} setTo={setTo} onApply={load} testid="ov" />;
  return (
    <div>
      <RangeBar from={from} setFrom={setFrom} to={to} setTo={setTo} onApply={load} testid="ov" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="overview-stats">
        <Stat label="Hours logged" value={`${ov.total_hours}h`} sub={`${ov.members} members · capacity ${ov.capacity_hours}h`} testid="stat-hours" />
        <Stat label="Utilization" value={`${ov.utilization_pct}%`} sub={`Billable ${ov.billable_pct}%`} testid="stat-utilization" tone={ov.utilization_pct < 50 ? "text-amber-600" : "text-emerald-600"} />
        <Stat label="Compliance rate" value={`${ov.compliance.compliance_rate}%`} sub={`${ov.compliance.violations} violations · ${ov.compliance.escalated} escalated`} testid="stat-compliance" tone={ov.compliance.compliance_rate < 70 ? "text-red-600" : "text-emerald-600"} />
        <Stat label="Pending reviews" value={ov.compliance.pending_reviews} sub="Point adjustments awaiting HR" testid="stat-pending" tone={ov.compliance.pending_reviews ? "text-amber-600" : ""} />
        <Stat label="Timesheets submitted" value={ov.timesheets.submitted} sub={`${ov.timesheets.approved} approved · ${ov.timesheets.on_time_pct}% on time`} testid="stat-timesheets" />
        <Stat label="Avg late (clock-in)" value={`${ov.attendance.avg_late_minutes}m`} sub={`${ov.attendance.punch_days} punch days`} testid="stat-late" />
        <Stat label="WFH days" value={ov.attendance.wfh_days} sub={`${ov.attendance.leave_requests} leave requests`} testid="stat-wfh" />
        <div className="border p-4" data-testid="top-violators">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Top violators</p>
          {ov.top_violators.length === 0 && <p className="text-sm text-muted-foreground">None 🎉</p>}
          {ov.top_violators.map((t) => (
            <div key={t.user_id} className="flex justify-between text-sm py-0.5">
              <PersonChip userId={t.user_id} name={t.user_name} className="truncate" />
              <Badge variant="outline" className="rounded-none border-0 bg-red-500/15 text-red-600">{t.count}</Badge>
            </div>
          ))}
        </div>
      </div>
      <div className="border p-4" data-testid="trends-chart">
        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Daily hours, submissions & violations</p>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={trends}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="hours" name="Hours" fill="hsl(220 70% 55%)" />
            <Bar dataKey="billable" name="Billable h" fill="hsl(150 60% 45%)" />
            <Line type="monotone" dataKey="violations" name="Violations" stroke="hsl(0 75% 55%)" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="submissions" name="Submissions" stroke="hsl(40 90% 50%)" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function EstimatesTab({ wsId }) {
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState(null);
  const load = () => api.get(`/workspaces/${wsId}/hr-dashboard/estimate-vs-actual?from=${from}&to=${to}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErrorMessage(e)));
  useEffect(() => { load(); }, [wsId]); // eslint-disable-line
  return (
    <div>
      <RangeBar from={from} setFrom={setFrom} to={to} setTo={setTo} onApply={load} testid="est" />
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5" data-testid="est-summary">
          <Stat label="Tasks tracked" value={data.summary.tasks_tracked} sub={`${data.summary.tasks_estimated} with estimates`} testid="est-stat-tracked" />
          <Stat label="Overruns" value={data.summary.overruns} testid="est-stat-overruns" tone={data.summary.overruns ? "text-red-600" : "text-emerald-600"} />
          <Stat label="Avg estimate variance" value={data.summary.avg_overrun_pct != null ? `${data.summary.avg_overrun_pct}%` : "—"} testid="est-stat-avg" />
          <Stat label="On-time completion" value={`${data.summary.on_time_completion_pct}%`} sub={`${data.summary.tasks_due_done} done tasks with due dates`} testid="est-stat-ontime" />
        </div>
      )}
      <div className="overflow-x-auto border">
        <table className="w-full text-sm" data-testid="est-table">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-2.5 font-medium">Task</th>
              <th className="p-2.5 font-medium">Status</th>
              <th className="p-2.5 font-medium text-right">Est h</th>
              <th className="p-2.5 font-medium text-right">Actual h</th>
              <th className="p-2.5 font-medium text-right">Variance</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.task_id} className="border-t" data-testid={`est-row-${r.task_id}`}>
                <td className="p-2.5 max-w-xs truncate">{r.task_title}</td>
                <td className="p-2.5"><Badge variant="outline" className="rounded-none">{r.status}</Badge></td>
                <td className="p-2.5 text-right font-mono">{r.est_hours || "—"}</td>
                <td className="p-2.5 text-right font-mono">{r.actual_hours}</td>
                <td className={`p-2.5 text-right font-mono ${r.overrun_pct > 0 ? "text-red-600" : "text-emerald-600"}`}>
                  {r.overrun_pct != null ? `${r.overrun_pct > 0 ? "+" : ""}${r.overrun_pct}%` : "—"}
                </td>
              </tr>
            ))}
            {data && data.rows.length === 0 && <tr><td className="p-4 text-muted-foreground" colSpan={5}>No task time logged in this range.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DigestTab({ wsId }) {
  const [digests, setDigests] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/workspaces/${wsId}/hr-dashboard/ai-digest`).then((r) => setDigests(r.data)).catch(() => {});
  useEffect(() => { load(); }, [wsId]); // eslint-disable-line
  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/hr-dashboard/ai-digest`);
      toast.success("AI digest generated");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  const latest = digests[0];
  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-muted-foreground">Auto-generated every Monday 07:30 UTC and emailed to HR/admins.</p>
        <Button className="rounded-none" onClick={generate} disabled={busy} data-testid="generate-digest-btn">
          {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {busy ? "Generating…" : "Generate now"}
        </Button>
      </div>
      {!latest && <p className="text-sm text-muted-foreground" data-testid="digest-empty">No digest yet — generate your first one.</p>}
      {latest && (
        <div className="border p-5" data-testid="digest-latest">
          <DigestContent digest={latest} />
        </div>
      )}
      {digests.length > 1 && (
        <div className="mt-5" data-testid="digest-history">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Digest history</p>
          {digests.slice(1).map((d) => (
            <details key={d.id} className="border-t" data-testid={`digest-hist-${d.id}`}>
              <summary className="py-2 text-sm flex justify-between cursor-pointer hover:bg-muted/30 px-1">
                <span className="truncate">{d.content.headline}</span>
                <span className="font-mono text-xs text-muted-foreground shrink-0">{d.from} → {d.to}</span>
              </summary>
              <div className="border border-border p-4 mb-3 bg-muted/10">
                <DigestContent digest={d} />
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function DigestContent({ digest }) {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-mono text-muted-foreground">{digest.from} → {digest.to}</p>
        <h3 className="text-lg font-bold mt-1">{digest.content.headline}</h3>
        <p className="text-sm mt-2">{digest.content.summary}</p>
      </div>
      {["highlights", "concerns", "recommendations"].map((k) => (
        (digest.content[k] || []).length > 0 && (
          <div key={k}>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{k}</p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              {digest.content[k].map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          </div>
        )
      ))}
      {digest.stats?.top_violators?.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Top violators</p>
          <p className="text-sm">{digest.stats.top_violators.map((t) => `${t.user_name} (${t.count})`).join(" · ")}</p>
        </div>
      )}
    </div>
  );
}

function AskTab({ wsId }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [recent, setRecent] = useState([]);
  const load = () => api.get(`/workspaces/${wsId}/hr-dashboard/ask`).then((r) => setRecent(r.data)).catch(() => {});
  useEffect(() => { if (wsId) load(); }, [wsId]); // eslint-disable-line
  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true); setAnswer(null);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/hr-dashboard/ask`, { question: q.trim() });
      setAnswer(data); setQ(""); load();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  const togglePin = async (r) => {
    try {
      await api.post(`/workspaces/${wsId}/hr-dashboard/ask/${r.id}/pin`, { pinned: !r.pinned });
      toast.success(r.pinned ? "Unpinned" : "Pinned — will be answered in every Monday digest");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  return (
    <div className="max-w-3xl">
      <p className="text-sm text-muted-foreground mb-3">Ask anything about the last 30 days of people data — e.g. "Who is most overloaded?", "Which department logs the least billable hours?", "Who keeps missing timesheets?"</p>
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !busy && ask()} placeholder="Ask a question about your team…" className="rounded-none" data-testid="ask-input" />
        <Button className="rounded-none" onClick={ask} disabled={busy || !q.trim()} data-testid="ask-submit">
          {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
      {busy && <p className="text-xs text-muted-foreground mt-3 animate-pulse" data-testid="ask-loading">Analyzing team data…</p>}
      {answer && (
        <div className="border p-4 mt-4 space-y-2" data-testid="ask-answer">
          <p className="text-xs text-muted-foreground italic">"{answer.question}"</p>
          <p className="text-sm whitespace-pre-wrap">{answer.answer}</p>
          {(answer.data_points || []).length > 0 && (
            <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-0.5">
              {answer.data_points.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          )}
        </div>
      )}
      {recent.length > 0 && (
        <div className="mt-6" data-testid="ask-recent">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Recent questions <span className="normal-case">(📌 pinned ones get fresh answers in every Monday digest)</span></p>
          {recent.map((r) => (
            <details key={r.id} className="border-t py-2 text-sm">
              <summary className="cursor-pointer">
                <span className={r.pinned ? "font-semibold" : ""}>{r.question}</span>
                <button
                  onClick={(e) => { e.preventDefault(); togglePin(r); }}
                  className={`ml-2 text-xs ${r.pinned ? "text-amber-600" : "text-muted-foreground hover:text-amber-600"}`}
                  title={r.pinned ? "Unpin from weekly digest" : "Pin to weekly digest"}
                  data-testid={`ask-pin-${r.id}`}
                >{r.pinned ? "📌 Pinned" : "Pin to digest"}</button>
              </summary>
              <p className="text-xs text-muted-foreground mt-1.5 whitespace-pre-wrap">{r.answer}</p>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function EmployeeAITab({ wsId }) {
  const [members, setMembers] = useState([]);
  const [uid, setUid] = useState("");
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(daysAgo(0));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/members`).then((r) => setMembers(r.data || [])).catch(() => {});
  }, [wsId]);

  const generate = async (force = false) => {
    if (!uid) { toast.error("Pick a team member first"); return; }
    setBusy(true);
    if (force) setResult(null);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/hr-dashboard/employee-summary`, {
        user_id: uid, from, to, force,
      });
      setResult(data);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  const s = result?.stats;
  return (
    <div className="max-w-3xl" data-testid="empai-tab">
      <p className="text-sm text-muted-foreground mb-3">AI performance summary grounded in a member's real hours, tasks, timesheets, attendance, compliance and SOP execution.</p>
      <div className="flex items-center gap-2 flex-wrap mb-5">
        <Select value={uid} onValueChange={setUid}>
          <SelectTrigger className="rounded-none w-56" data-testid="empai-member"><SelectValue placeholder="Pick a team member…" /></SelectTrigger>
          <SelectContent>
            {members.map((m) => (
              <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email || m.user_id}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none w-40" data-testid="empai-from" />
        <span className="text-sm text-muted-foreground">→</span>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none w-40" data-testid="empai-to" />
        <Button className="rounded-none" onClick={() => generate(false)} disabled={busy || !uid} data-testid="empai-generate">
          {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {busy ? "Analyzing…" : "Generate summary"}
        </Button>
      </div>

      {result && (
        <div className="border p-5 space-y-4" data-testid="empai-result">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-lg font-bold">{result.user?.name || result.user?.email || result.user_id}</h3>
              <p className="text-xs font-mono text-muted-foreground">{result.from} → {result.to}{result.reused ? " · cached" : ""}</p>
            </div>
            <Button size="sm" variant="outline" className="rounded-none" onClick={() => generate(true)} disabled={busy} data-testid="empai-regenerate">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate
            </Button>
          </div>
          {s && (
            <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="empai-stats">
              <Badge variant="outline" className="rounded-none">{s.hours_logged}h logged</Badge>
              <Badge variant="outline" className="rounded-none">{s.billable_hours}h billable</Badge>
              <Badge variant="outline" className="rounded-none">{s.tasks_completed} tasks done</Badge>
              <Badge variant="outline" className="rounded-none">{s.tasks_open} open</Badge>
              <Badge variant="outline" className="rounded-none">{s.compliance_violations} violations</Badge>
              <Badge variant="outline" className="rounded-none">{s.approved_leaves} leaves</Badge>
              {s.timesheet_on_time_pct != null && <Badge variant="outline" className="rounded-none">{s.timesheet_on_time_pct}% timesheets on time</Badge>}
              <Badge variant="outline" className="rounded-none">{s.sop_runs_completed}/{s.sop_runs} SOP runs</Badge>
            </div>
          )}
          <p className="text-sm" data-testid="empai-summary">{result.content?.summary}</p>
          {[["strengths", "text-emerald-600"], ["risks", "text-red-600"], ["recommendations", "text-blue-600"]].map(([k, tone]) => (
            (result.content?.[k] || []).length > 0 && (
              <div key={k}>
                <p className={`text-xs uppercase tracking-wide mb-1 ${tone}`}>{k}</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {result.content[k].map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}
